import React, {
  CSSProperties,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import mapboxgl from 'mapbox-gl';

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Button } from '@mui/material';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Grid } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { TextProps } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectInColorBlindMode,
  selectMaximize,
  selectShortestAvailableBin,
  selectTargetDate,
  setTimeOfDay,
} from 'state/workflowSlice';

import { CHART_RESIZE_DEBOUNCE_MS } from 'appConstants';
import { ScreenshotChartContext } from 'features/chart/ScreenshotChart';
import { distanceUnit } from 'features/common/utils';
import { MapContext } from 'features/map/MapContext';
import {
  addQueuesToMap,
  getHoverOffsetFromMapMouseMove,
  setMapQueuesState,
  updateHoverOffsetOnMap,
} from 'features/workflow_queuing/queuingMapBehaviour';
import { debounce } from 'lodash';
import { chart_background_color, workspace_color } from 'theme/cemTheme';

import { formatTimeOfDay } from '../common/datetimeUtils';
import {
  UseRouteInfoReturnType,
  useRouteInfo,
} from '../common/hooks/useRouteInfo';
import { ReactComponent as EndIcon } from '../map/icons/queue_position_marker.svg';
import { ReactComponent as StartIcon } from '../map/icons/queue_route_start_icon.svg';
import LoadingSpinner from '../spinner/Spinner';
import { NoRouteSelectedPanel } from '../workflow_common/RouteInstructions/NoRouteSelectedPanel';
import { VisualisationRegion } from '../workflow_nrt/WorkflowRecent';
import {
  QueueChartTooltipContents,
  TooltipData,
} from './QueueChartTooltipContents';
import { QueueMapTooltipContents } from './QueueMapTooltipContents';
import {
  RouteQueueTagma,
  colorRampForColorStatus,
  get_congestion,
  get_minute_offset,
  get_offset_end,
  get_offset_start,
  get_queue_id,
  get_timestamp,
  kQueueOutlineColor,
  nullCongestionColor,
} from './common';
import { useQueueDataProvider } from './queueDataProvider';
import { SvgBarberPoleMask, SvgRect } from './svgElements';

const PX_LIMI_BUFFER = 50;
const QUEUE_CHART_CONTAINER_MARGIN = 3;
const QUEUE_CHART_MARGINS = { top: 10, bottom: 40, left: 60, right: 20 };

type RenderableSeg = RouteQueueTagma & {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  color: string;
  min_offset: number;
};

const tooltipStyles: CSSProperties = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: workspace_color,
  padding: '0',
  color: 'white',
  zIndex: 900,
  textAlign: 'left',
};

const axisLabelBaseStyles: Partial<TextProps> = {
  fill: 'white',
  fontSize: 14,
  textAnchor: 'middle',
};

const axisTicksBaseStyles: Partial<TextProps> = {
  fill: 'white',
  fontSize: 11,
  textAnchor: 'middle',
};

const compose = (scale, accessor) => (data) => scale(accessor(data));
const kQueueChartContainerId = 'queue-chart-container';

export function QueuingChartWithRegion({
  selectedTs,
  setSelectedTs,
  selectedMin,
  setSelectedMin,
  isSelectionSticky,
  setSelectionSticky,
  setMapTooltipContent,
  handleEnableHover,
}: {
  selectedTs: string | null;
  setSelectedTs: (v: string | null) => void;
  selectedMin: number | null;
  setSelectedMin: (v: number | null) => void;
  isSelectionSticky: boolean;
  setSelectionSticky: (v: boolean) => void;
  setMapTooltipContent: (child: React.ReactNode) => void;
  handleEnableHover: (val: boolean) => void;
}) {
  const chartId = useId();
  const clipPathId = `graph_region-${chartId}`;
  const barberPoleId = `barberpole-mask-${chartId}`;
  const dispatch = useAppDispatch();

  const userProject = useAppSelector(selectCurrentProjectInfo);
  const targetDate = useAppSelector(selectTargetDate);
  const binSize = useAppSelector(selectShortestAvailableBin);
  const maximize = useAppSelector(selectMaximize);
  const colorBlindMode = useAppSelector(selectInColorBlindMode);
  const colormap = colorRampForColorStatus(colorBlindMode);
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>();
  const refTooltipTimeout = useRef(null);
  const refChartContainer = useRef(null);
  const prevHoverRef = useRef<boolean | null>(null);
  const isInScreenshotMode = useContext(ScreenshotChartContext);
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    // TooltipInPortal is rendered in a separate child of <body /> and positioned
    // with page coordinates which should be updated on scroll. consider using
    // Tooltip or TooltipWithBounds if you don't need to render inside a Portal
    scroll: false,
    detectBounds: true,
  });

  const { refMap, mapReady, mapStyle } = useContext(MapContext);
  const routeInfo = useRouteInfo();
  const refRouteInfo = useRef<UseRouteInfoReturnType>(routeInfo);
  const { currentData: queue_data } = useQueueDataProvider();
  const feature_collection = queue_data?.tagmas;
  const segment_times = queue_data?.segment_times;

  const data = segment_times?.features || [];
  const [hoverOffset, setHoverOffset] = useState<null | number>(null);
  const [hoverQueueId, setHoverQueueId] = useState<null | number>(null);

  const [containerWidth, setContainerWidth] = useState<null | number>(null);
  const [containerHeight, setContainerHeight] = useState<null | number>(null);
  const [routeLength, setRouteLength] = useState<null | number>(null);
  const refSelectionSticky = useRef<boolean>(isSelectionSticky);

  useEffect(() => {
    refSelectionSticky.current = isSelectionSticky;
    if (!isSelectionSticky) dispatch(setTimeOfDay(undefined));
  }, [isSelectionSticky]);

  const setSticky = (value: boolean) => {
    setSelectionSticky(value);
    refSelectionSticky.current = value;
  };

  const { queues, timestamps } = useMemo(() => {
    const queuess = {};
    const timestampss = {};
    for (let i = 0; i < data.length; i++) {
      const queue_id = get_queue_id(data[i]);
      if (queue_id !== null) {
        queuess[queue_id] = queuess[queue_id] || [];
        queuess[queue_id].push(data[i]);
      }
      const ts = get_timestamp(data[i]);
      timestampss[ts] = timestampss[ts] || [];
      timestampss[ts].push(data[i]);
    }
    return { queues: queuess, timestamps: timestampss };
  }, [data]);

  const showTooltips = (tagma, top, left) => {
    showTooltip({
      tooltipData: { tagma },
      tooltipTop: top,
      tooltipLeft: left,
    });
    setMapTooltipContent(
      <QueueMapTooltipContents tooltipData={{ tagma }} queues={queues} />,
    );
  };
  const hideTooltips = () => {
    if (tooltipOpen) hideTooltip();
    setMapTooltipContent(undefined);
  };

  const clearSelectionState = () => {
    setHoverQueueId(null);
    setSelectedTs(null);
    setSelectedMin(null);
    setSticky(false);
  };

  const handleWindowResize = () => {
    const el = refChartContainer.current;
    if (!el) return;

    if (el) {
      const { width, height } = el.getBoundingClientRect();
      if (width !== 0 && height !== 0) {
        setContainerHeight(height);
        setContainerWidth(width);
      }
      // console.log(height, width);
    }
  };
  const width = Math.floor(containerWidth - 2 * QUEUE_CHART_CONTAINER_MARGIN);
  const height = Math.floor(containerHeight - 2 * QUEUE_CHART_CONTAINER_MARGIN);

  useEffect(() => {
    let inner_route_length;
    if (routeInfo.feature) {
      inner_route_length = routeInfo.feature.properties.route_length * 0.001;
    }
    if (inner_route_length !== routeLength) {
      setRouteLength(inner_route_length);
    }
  }, [routeInfo]);

  const { minOffset, maxOffset } = useMemo(() => {
    if (!data.length) return { minOffset: 0, maxOffset: 24 * 60 };

    let min = Infinity;
    let max = -Infinity;

    data.forEach((d) => {
      const offset = get_minute_offset(d, targetDate);
      if (offset != null) {
        min = Math.min(min, offset);
        max = Math.max(max, offset);
      }
    });

    return {
      minOffset: Math.floor(min / binSize) * binSize,
      maxOffset: Math.ceil(max / binSize) * binSize + binSize, // adding one extra binSize to add padding on the right
    };
  }, [data, targetDate, binSize]);

  // Then we'll create some bounds
  const result = useMemo(() => {
    // console.log('Redo scales...');
    const xMax = width - QUEUE_CHART_MARGINS.left - QUEUE_CHART_MARGINS.right;
    const yMax = height - QUEUE_CHART_MARGINS.top - QUEUE_CHART_MARGINS.bottom;

    const xDomain = [];
    for (let t = minOffset; t <= maxOffset; t += binSize) {
      xDomain.push(t);
    }
    const xScale = scaleBand({
      range: [0, xMax],
      round: false,
      domain: xDomain,
      padding: 0,
      align: 0,
    });
    const yScale = scaleLinear({
      range: [yMax, 0],
      round: true,
      domain: [0, routeLength],
    });
    const congestionScale = scaleLinear({
      domain: colormap.stops(),
      range: colormap.colors_hex(),
    });

    return { xMax, yMax, xDomain, xScale, yScale, congestionScale };
  }, [
    width,
    height,
    QUEUE_CHART_MARGINS,
    binSize,
    routeLength,
    colormap,
    minOffset,
    maxOffset,
  ]);

  const { xMax, yMax, xDomain, xScale, yScale, congestionScale } = result;
  const xPoint = compose(xScale, (t) => get_minute_offset(t, targetDate));
  const y_high = compose(yScale, get_offset_end);
  const y_low = compose(yScale, get_offset_start);

  const cong_color = (d) => {
    const congestion = get_congestion(d);
    if (congestion !== null) {
      return congestionScale(congestion);
    } else {
      return nullCongestionColor;
    }
  };

  const renderableSegs = useMemo<RenderableSeg[]>(() => {
    const ymin = yScale(0);
    const segs = (queue_data?.segment_times?.features || []).map((v) => {
      const y2 = y_low(v);
      const min_offset = get_minute_offset(v, targetDate);
      const x = xScale(min_offset);
      let bw = xScale.bandwidth() - 1.0;
      if (min_offset % 60 === 45) {
        bw -= 1.0;
      }
      return {
        x1: x,
        x2: x + bw,
        y1: y_high(v),
        y2: y2 >= ymin ? ymin + 20 : y2, // extend bottom segment outside of the clip path for a tidier look
        color: cong_color(v),
        min_offset,
        ...v,
      } as RenderableSeg;
    });
    // console.debug(
    //   `Recalc renderable segs ${segs.length} ${xScale.domain()}`,
    // );
    return segs;
  }, [queue_data, xScale, yScale]);

  const updateTooltip = (event, tagma, hOffset) => {
    // TooltipInPortal expects coordinates to be relative to containerRef
    // localPoint returns coordinates relative to the nearest SVG, which
    // is what containerRef is set to in this example.

    function leftFromX(x) {
      let left = x + QUEUE_CHART_MARGINS.left + xScale.bandwidth();
      left = Math.max(Math.min(left, xMax), 0);
      return left;
    }

    if (!refSelectionSticky.current && tagma) {
      const eventSvgCoords = localPoint(event);
      const top = eventSvgCoords?.y;
      showTooltips(tagma, top, leftFromX(xPoint(tagma)));
    } else if (selectedTs) {
      const ts_tags = timestamps[selectedTs];
      const offset = hOffset;
      if (ts_tags) {
        for (let i = 0; i < ts_tags.length; i++) {
          if (
            get_offset_start(ts_tags[i]) <= offset &&
            get_offset_end(ts_tags[i]) >= offset
          ) {
            showTooltips(ts_tags[i], yMax, leftFromX(xScale(selectedMin)));
            break;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (mapReady && refMap.current) {
      updateHoverOffsetOnMap(
        refMap.current,
        mapStyle,
        hoverOffset,
        routeInfo.feature,
      );
    }
  }, [hoverOffset, mapReady, routeInfo.feature]);

  const handleHover = (dist: number | undefined) => {
    // dist === undefined when routeSelection is undefined
    const nextHover = dist !== undefined ? dist > PX_LIMI_BUFFER : true;
    // event deduplication
    if (prevHoverRef.current !== nextHover) {
      prevHoverRef.current = nextHover;
      handleEnableHover(nextHover);
    }
  };

  const onMapMouseMoveHandler = (event: mapboxgl.MapMouseEvent) => {
    const { localHoverOffset, dist } = getHoverOffsetFromMapMouseMove(
      refMap.current,
      event,
      refRouteInfo.current.feature,
    );
    handleHover(dist);
    setHoverOffset(localHoverOffset);
    updateTooltip(event, undefined, localHoverOffset);
  };

  useEffect(() => {
    if (mapReady && !isInScreenshotMode) {
      refMap.current.on('mousemove', onMapMouseMoveHandler);
    }
    return () => {
      if (refMap.current) {
        refMap.current.off('mousemove', onMapMouseMoveHandler);
      }
    };
  }, [mapReady]);

  useEffect(() => {
    refRouteInfo.current = routeInfo;
  }, [routeInfo]);

  useEffect(() => {
    if (mapReady && refMap.current) {
      addQueuesToMap(
        refMap.current,
        mapStyle,
        segment_times,
        feature_collection,
        selectedTs,
        colormap,
        refRouteInfo.current.feature,
      );
    }
  }, [mapReady, segment_times, colormap]);

  useEffect(() => {
    setMapQueuesState(refMap.current, selectedTs, hoverQueueId);
  }, [selectedTs, hoverQueueId]);

  const handleGraphMouseMoveBase = (
    event: React.MouseEvent,
    tagma?: RenderableSeg,
  ) => {
    const newOffset = yScale.invert(
      localPoint(event).y - QUEUE_CHART_MARGINS.top,
    );
    if (newOffset >= 0) {
      setHoverOffset(newOffset);
    } else {
      setHoverOffset(null);
    }
    if (!refSelectionSticky.current) {
      if (tagma) {
        setHoverQueueId(get_queue_id(tagma));
        // console.log(tagma, queues[get_queue_id(tagma)]);
        setSelectedTs(tagma.properties.timestep_at_tz);
        setSelectedMin(tagma.min_offset);
      } else {
        clearSelectionState();
      }
    }
  };

  const handleMouseLeave = (event) => {
    const local = localPoint(event);
    if (
      !(
        local.x > xMax ||
        local.x < QUEUE_CHART_MARGINS.left ||
        local.y > yMax ||
        local.y < QUEUE_CHART_MARGINS.top
      )
    ) {
      // console.log('Mouse is over tooltip, not updating hover');
      return;
    }

    setHoverOffset(null);
    if (!refSelectionSticky.current) {
      clearSelectionState();
    }
    refTooltipTimeout.current = window.setTimeout(() => {
      if (!refSelectionSticky.current) {
        hideTooltips();
      }
    }, 300);
  };

  const makeHandleGraphMouseMoveOnTagma = (tagma: RenderableSeg) => {
    return (event: React.MouseEvent) => {
      handleGraphMouseMoveBase(event, tagma);
      if (refTooltipTimeout.current) {
        clearTimeout(refTooltipTimeout.current);
        refTooltipTimeout.current = undefined;
      }
      updateTooltip(event, tagma, hoverOffset);
    };
  };

  const makeHandleGraphMouseClickOnTagma = (tagma: RenderableSeg) => {
    return (event: React.MouseEvent) => {
      if (get_timestamp(tagma) === selectedTs) {
        setSticky(true);
        dispatch(setTimeOfDay(get_minute_offset(tagma, targetDate)));
      } else if (isSelectionSticky) {
        setSticky(false);
        makeHandleGraphMouseMoveOnTagma(tagma)(event);
      } else {
        setSticky(true);
      }
    };
  };

  useEffect(() => {
    if (isSelectionSticky && !isInScreenshotMode) {
      updateTooltip(undefined, undefined, hoverOffset);
    }
  }, [hoverOffset, selectedTs, isInScreenshotMode]);

  const changeBinSelectionByOffset = (offset) => {
    const ordered_timestamps = Object.keys(timestamps);
    ordered_timestamps.sort();
    const ts =
      ordered_timestamps[ordered_timestamps.indexOf(selectedTs) + offset];
    const min = selectedMin + offset * binSize;
    setSelectedTs(ts);
    setSelectedMin(min);
  };

  useEffect(() => {
    if (!refChartContainer.current) return undefined;

    const debouncedResize = debounce(
      handleWindowResize,
      CHART_RESIZE_DEBOUNCE_MS,
    );

    const resizeObserver = new ResizeObserver(debouncedResize);
    resizeObserver.observe(refChartContainer.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    handleWindowResize();
  }, [maximize]);

  useEffect(() => {
    if (!routeInfo.basicRouteInfo.waypointSegments || !segment_times) {
      hideTooltips();
      clearSelectionState();
    }
  }, [routeInfo, segment_times]);

  let graph;
  if (containerWidth) {
    graph = (
      <>
        <svg
          ref={containerRef}
          width={width}
          height={height}
          onMouseLeave={handleMouseLeave}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          <SvgBarberPoleMask chartId={chartId} />
          <Group left={QUEUE_CHART_MARGINS.left} top={QUEUE_CHART_MARGINS.top}>
            <Grid
              top={0}
              left={0}
              xScale={xScale}
              yScale={yScale}
              width={xMax}
              height={yMax}
              stroke="white"
              strokeOpacity={0.1}
              xOffset={xScale.bandwidth() / 2}
            />
            <rect
              x={0}
              y={0}
              width={width}
              height={height}
              opacity={0}
              onMouseMove={handleGraphMouseMoveBase}
              onMouseLeave={handleMouseLeave}
            />
            <clipPath id={clipPathId}>
              <rect
                x={xScale.range()[0]}
                y={yScale.range()[1]}
                width={xScale.range()[1] - xScale.range()[0]}
                height={yScale.range()[0] - yScale.range()[1]}
                opacity={0}
              />
            </clipPath>
            <g clipPath={`url(#${clipPathId})`}>
              {renderableSegs.map((tagma, index) => {
                return (
                  <SvgRect
                    x1={tagma.x1}
                    x2={tagma.x2}
                    y1={tagma.y1 - 1}
                    y2={tagma.y2}
                    fill={tagma.color}
                    key={index} // eslint-disable-line react/no-array-index-key
                    mask={
                      tagma.properties.arthropod_id
                        ? `url(#${barberPoleId})`
                        : undefined
                    }
                    zigHeight={5}
                    opacity={
                      get_timestamp(tagma) === selectedTs // eslint-disable-line no-nested-ternary
                        ? 1.0
                        : !selectedTs // eslint-disable-line no-nested-ternary
                          ? 1.0
                          : isSelectionSticky
                            ? 0.6
                            : 0.5
                    }
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={makeHandleGraphMouseMoveOnTagma(tagma)}
                    onClick={makeHandleGraphMouseClickOnTagma(tagma)}
                  />
                );
              })}
            </g>
            <AxisBottom
              top={yMax}
              left={-0.5 * xScale.bandwidth()}
              scale={xScale}
              tickFormat={formatTimeOfDay}
              stroke="white"
              tickStroke="white"
              tickLabelProps={{
                ...axisTicksBaseStyles,
                dy: '0em',
              }}
              tickValues={xDomain.filter((_, i) => i % 12 === 0)} // show every 3 hours on the x axis
            />

            <AxisLeft
              scale={yScale}
              stroke="white"
              tickStroke="white"
              label={`Distance along route (${distanceUnit(
                userProject.uses_metric,
              )}) →`}
              labelProps={axisLabelBaseStyles}
              tickLabelProps={{
                ...axisTicksBaseStyles,
                dx: '-1em',
              }}
            />
            {hoverOffset && (
              <line
                id="hoverOffsetLine"
                x1={0}
                x2={xMax}
                y1={yScale(hoverOffset)}
                y2={yScale(hoverOffset)}
                stroke="white"
                opacity={0.85}
                strokeWidth="2px"
                style={{ pointerEvents: 'none' }}
              />
            )}
            <line
              id="midnightLine1"
              x1={xScale(0)}
              x2={xScale(0)}
              y1={0}
              y2={yMax}
              stroke="white"
              strokeWidth="2px"
            />
            <line
              id="midnightLine2"
              x1={xScale(24 * 60)}
              x2={xScale(24 * 60)}
              y1={0}
              y2={yMax}
              stroke="white"
              strokeWidth="2px"
            />
            <g clipPath={`url(#${clipPathId})`}>
              {Object.values(queues).map((el, index) => {
                const tagmas = el as RouteQueueTagma[];
                const start = tagmas[0];
                const end = tagmas[tagmas.length - 1];
                return (
                  <SvgRect
                    x1={xPoint(start)}
                    x2={xPoint(start) + xScale.bandwidth()}
                    y1={y_high(start)}
                    y2={y_low(end)}
                    key={index} // eslint-disable-line react/no-array-index-key
                    fill="none"
                    stroke={kQueueOutlineColor}
                    strokeWidth="1px"
                    zigHeight={5}
                  />
                );
              })}
            </g>
            {selectedMin && (
              <>
                <line
                  id="selectedMinLineL"
                  x1={xScale(selectedMin)}
                  x2={xScale(selectedMin)}
                  y1={0}
                  y2={yMax}
                  stroke="white"
                  opacity={1}
                  strokeWidth="2px"
                  style={{ pointerEvents: 'none' }}
                />
                <line
                  id="selectedMinLineR"
                  x1={xScale(selectedMin) + xScale.bandwidth()}
                  x2={xScale(selectedMin) + xScale.bandwidth()}
                  y1={0}
                  y2={yMax}
                  stroke="white"
                  opacity={1}
                  strokeWidth="2px"
                  style={{ pointerEvents: 'none' }}
                />
              </>
            )}

            <line
              id="axisLeftLine"
              x1={0}
              x2={0}
              y1={0}
              y2={yMax}
              stroke="white"
              strokeWidth="2px"
            />
            <line
              id="axisBottomLine"
              x1={0}
              x2={xMax}
              y1={yMax}
              y2={yMax}
              stroke="white"
              strokeWidth="2px"
            />
            <g
              transform={`translate(${selectedMin ? xScale(selectedMin) + xScale.bandwidth() / 2 : (axisLabelBaseStyles.fontSize as number) - QUEUE_CHART_MARGINS.left}, 0)`}
            >
              <g
                transform="translate(-13, -13)"
                style={{ pointerEvents: 'none' }}
              >
                <circle fill={chart_background_color} cx={13} cy={13} r={9} />
                <EndIcon width="26px" height="26px" />
              </g>
              <g
                transform={`translate(-15,${yMax - 15})`}
                style={{ pointerEvents: 'none' }}
              >
                <circle fill={chart_background_color} cx={15} cy={15} r={10} />
                <StartIcon width="30px" height="30px" />
              </g>
            </g>
          </Group>
        </svg>
        <div
          style={{
            position: 'absolute',
            left: QUEUE_CHART_MARGINS.left + xMax / 2,
            top:
              yMax +
              QUEUE_CHART_MARGINS.top +
              (axisLabelBaseStyles as any).fontSize * 1.5,
            transform: 'translateX(-50%)',
            fontSize: 14,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {isSelectionSticky && (
            <Button
              style={{ height: '1.5rem', width: '7em' }}
              startIcon={<ArrowBack />}
              disabled={xDomain[0] === selectedMin}
              onClick={() => {
                changeBinSelectionByOffset(-1);
              }}
            >
              Previous
            </Button>
          )}
          <span
            style={{
              height: '1.5rem',
              display: 'inline-flex',
              alignItems: 'center',
              paddingLeft: '2em',
              paddingRight: '2em',
            }}
          >
            Time of Day
          </span>
          {isSelectionSticky && (
            <Button
              style={{ height: '1.5rem', width: '7em' }}
              endIcon={<ArrowForward />}
              disabled={xDomain[xDomain.length - 2] <= selectedMin}
              onClick={() => {
                changeBinSelectionByOffset(1);
              }}
            >
              Next
            </Button>
          )}
        </div>
        {tooltipOpen && tooltipData && !isInScreenshotMode && (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            style={{
              ...tooltipStyles,
              pointerEvents: isSelectionSticky ? 'all' : 'none',
            }}
            key={Math.random()}
          >
            <QueueChartTooltipContents
              tooltipData={tooltipData}
              hoverOffset={hoverOffset}
              queues={queues}
              timestamps={timestamps}
              onCloseClick={
                isSelectionSticky
                  ? () => {
                      setSticky(false);
                      hideTooltips();
                      setHoverQueueId(null);
                      setSelectedTs(null);
                      setSelectedMin(null);
                    }
                  : undefined
              }
            />
          </TooltipInPortal>
        )}
      </>
    );
  }

  const ready = renderableSegs.length > 0;

  let pure_chart;

  if (!containerWidth) {
    handleWindowResize();
  }

  if (ready) {
    pure_chart = (
      <div
        className={kQueueChartContainerId}
        ref={refChartContainer}
        style={{
          position: 'relative',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {graph}
      </div>
    );
  } else {
    pure_chart = (
      <div
        className={kQueueChartContainerId}
        ref={refChartContainer}
        style={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <LoadingSpinner large />
      </div>
    );
  }

  return (
    <VisualisationRegion
      graph_title="Queuing"
      unselected_state_fn={NoRouteSelectedPanel}
    >
      {pure_chart}
    </VisualisationRegion>
  );
}
